import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { RegisterPage, orNone, valueToLabel } from "./RegisterPage";
import type { ColumnDef, FieldDef } from "./RegisterPage";
import { assets } from "../../../lib/resources";
import type { Asset } from "../../../lib/resources";
import type { ComplianceFilter } from "./assetFilters";
import { dueDateTone, liftStatusDot, LIFT_TONE_META } from "../../../lib/liftStatus";
import { StatusPill, toneTextClass } from "../../../components/StatusPill";
import { formatDate } from "../../../lib/format";
// The quote calculator's own frequency list (src/lib/quotePricing.ts), the canonical client-side
// source: reused here rather than hand-typed a second time, so the register's Service frequency
// select cannot quietly drift from what the calculator (and the Frequency enum it mirrors) offers.
import { FREQUENCY_OPTIONS } from "../../../lib/quotePricing";

const COMPLIANCE_FILTERS: { value: ComplianceFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "green", label: "Green" },
  { value: "amber", label: "Amber" },
  { value: "red", label: "Red" },
];

// Shared option lists (also used by the lift detail page edit form, so they stay in one place).
export const ASSET_KIND_OPTIONS = [
  { value: "lift", label: "Lift" },
  { value: "ceiling_hoist", label: "Ceiling hoist" },
  { value: "stand_aid", label: "Stand aid" },
  { value: "treatment_table", label: "Treatment table" },
  { value: "other", label: "Other" },
  // Session ACV: a gate / door / barrier materialised from a gates-only tiered contract.
  // Without this entry the detail page showed the raw machine key and the edit form's
  // class select rendered blank (the class was unrepresentable).
  { value: "additional_item", label: "Additional item" },
];

export const FREQUENCY: { value: string; label: string }[] = [
  { value: "", label: "Not set" },
  ...FREQUENCY_OPTIONS,
];

// Drive types (the maintenance-checklist filter); shown read-only + editable on the detail page.
export const DRIVE_TYPE = [
  { value: "", label: "Not set" },
  { value: "hydraulic", label: "Hydraulic" },
  { value: "electric_traction", label: "Electric traction" },
  { value: "both", label: "Both" },
];

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

// The next service due for a lift, colour-coded on the three-band window (Session AD): red when
// overdue or due within 30 days OR a past visit has been missed, amber when due 31 to 90 days out,
// green beyond. The date shown is the next UPCOMING visit (the server projects it from the contract
// commencement when nothing is on the calendar), so a behind-schedule lift reads "Overdue" in red
// while still showing when it will next be serviced. "No contract" when the site has no active
// contract, "Not scheduled" when it has one but no date could be derived. `now` is injectable so the
// colour is deterministic in tests. House rule: never blank / "--". Reused on the lift detail page.
export function NextServiceDueCell({ asset, now = new Date() }: { asset: Asset; now?: Date }) {
  const tone = asset.nextServiceTone;
  if (tone === "no_contract") return <span className="text-ele-body opacity-60">No contract</span>;
  if (tone === "none" || !asset.nextScheduledServiceAt) return <span className="text-ele-body opacity-60">Not scheduled</span>;
  // Colour on the three-band window (red within 30 days, amber 31-90, green beyond), with a missed
  // visit forcing red. The WORD "Overdue" is reserved for a genuinely missed visit (serviceOverdue):
  // a visit merely due within 30 days reads red but is upcoming, not overdue. (A past
  // nextScheduledServiceAt always sets serviceOverdue, so serviceOverdue is the exact overdue signal.)
  const red = asset.serviceOverdue || dueDateTone(asset.nextScheduledServiceAt, now) === "red";
  const cls = toneTextClass(red ? "red" : dueDateTone(asset.nextScheduledServiceAt, now) === "amber" ? "amber" : "green");
  const date = formatDate(asset.nextScheduledServiceAt);
  return <span className={cls}>{asset.serviceOverdue ? `Overdue. Next due ${date}` : date}</span>;
}

// The single status dot for a lift: red when LOLER or service is overdue/due-within-30 or a service
// visit has been missed, amber when either is due 31 to 90 days out, green otherwise (or not yet
// set). Pure logic lives in src/lib/liftStatus.ts.
export function LiftStatusDot({ asset, now = new Date() }: { asset: Asset; now?: Date }) {
  const tone = liftStatusDot(
    {
      lolerDue: asset.lolerDue ?? asset.lolerCertExpiry,
      nextServiceDue: asset.nextScheduledServiceAt ?? asset.nextServiceDue,
      serviceOverdue: asset.serviceOverdue,
    },
    now,
  );
  const meta = LIFT_TONE_META[tone];
  return (
    <span className="inline-flex items-center" title={meta.label} aria-label={`Status: ${meta.label}`}>
      <span className={`inline-block h-3 w-3 rounded-full ${meta.dot}`} aria-hidden />
    </span>
  );
}

// The LOLER division-of-responsibility note shown above the Lifts register (Session AA, Item 2).
// Exported so a test pins the wording: ELE does NOT carry out LOLER thorough examinations or issue
// LOLER records; it tracks due dates and stores what the examining body sends. House-rule clean.
export const LIFTS_RESPONSIBILITY_NOTE =
  "LOLER thorough examinations are carried out by an independent competent person appointed by the duty holder. ELE tracks examination due dates and stores the certificates and examination reports received from the examining body.";

// The meaningful "Type" for a row: the KIND of lift (passenger, goods, platform...), with a
// care-equipment class fallback so a hoist/stand aid/table is still distinguished from a lift.
// NB Type is NOT the drive: the drive (hydraulic / electric traction) is Asset.driveType and has
// its own column. Migrated lifts carry V1's drive words ("Hydraulic"/"Electric") IN liftType (the
// sheet only recorded the drive) until the office corrects each record -- e.g. ELE-078A's true
// type is a platform lift and hydraulic is its drive. Session ADZ.
export function assetTypeLabel(a: Asset): string {
  if (a.liftType && a.liftType.trim()) return a.liftType;
  if (a.assetClass && a.assetClass !== "lift") return valueToLabel(ASSET_KIND_OPTIONS, a.assetClass);
  return "None";
}

// The drive column label: the driveType enum (the maintenance-checklist filter), plain English.
export function assetDriveLabel(a: Asset): string {
  return valueToLabel(DRIVE_TYPE, a.driveType ?? "");
}

// The clean, scan-only Lifts list (Session AC; Drive column added Session ADZ, so the KIND of lift
// and its DRIVE are never conflated again -- the confusion that left ADW unable to match a
// historical cert to ELE-078A). No action buttons: every action moves to the lift detail page
// (/office/lifts/:id), reached by clicking a row. The Lift ID is a real link (keyboard focus +
// open-in-new-tab); it stops propagation so it does not double-fire the row click. Exported so a
// test pins the column set.
export const columns: ColumnDef<Asset>[] = [
  {
    key: "ele",
    label: "Lift ID",
    width: "12%",
    render: (a) => (
      <Link
        to={`/office/lifts/${a.id}`}
        onClick={(e) => e.stopPropagation()}
        className="font-medium text-ele-green hover:underline"
      >
        {orNone(a.eleNumber)}
      </Link>
    ),
    sortValue: (a) => a.eleNumber ?? "",
  },
  {
    key: "site",
    label: "Site",
    width: "21%",
    // Forward click-through to the site (Session ADI). stopPropagation so the site link does not
    // also fire the row's onRowClick (which opens the lift), exactly as the Lift ID cell above does.
    render: (a) =>
      a.site?.name ? (
        <Link
          to={`/office/sites/${encodeURIComponent(a.site.id)}`}
          onClick={(e) => e.stopPropagation()}
          className="text-ele-green underline-offset-2 hover:underline"
        >
          {a.site.name}
        </Link>
      ) : (
        orNone(a.site?.name)
      ),
    sortValue: (a) => a.site?.name ?? "",
  },
  { key: "liftType", label: "Type", width: "14%", render: (a) => assetTypeLabel(a), sortValue: (a) => assetTypeLabel(a) },
  {
    key: "drive",
    label: "Drive",
    width: "11%",
    // The STORED driveType only, never the checklist's runtime inference: "None" (muted) tells the
    // office the record is incomplete and to set the drive on the lift's edit form. Rendering the
    // inference here would hide that gap behind a value that is not on the record.
    render: (a) => {
      const label = assetDriveLabel(a);
      return label === "None" ? <span className="text-ele-body opacity-60">None</span> : label;
    },
    sortValue: (a) => assetDriveLabel(a),
  },
  { key: "freq", label: "Frequency", width: "10%", render: (a) => valueToLabel(FREQUENCY, a.frequency), sortValue: (a) => valueToLabel(FREQUENCY, a.frequency) },
  {
    key: "lastServiced",
    label: "Last serviced",
    width: "12%",
    render: (a) => {
      return a.lastServicedAt ? <span className="text-ele-strong">{formatDate(a.lastServicedAt)}</span> : <span className="text-ele-body opacity-60">Not recorded</span>;
    },
    sortValue: (a) => a.lastServicedAt ?? "",
  },
  {
    key: "nextDue",
    label: "Next due",
    width: "13%",
    render: (a) => <NextServiceDueCell asset={a} />,
    // Sort by the SAME date the cell shows (the resolved next scheduled service); a lift the cell
    // reads "Not scheduled"/"No contract" has no date and sorts last (Session AD Item 2 review:
    // the accessor must not fall back to the stale stored nextServiceDue the cell never displays).
    sortValue: (a) => a.nextScheduledServiceAt ?? "",
  },
  // The status dot has no sortable value (sort by Next due to order by urgency instead).
  { key: "status", label: "Status", width: "7%", render: (a) => <LiftStatusDot asset={a} /> },
];

// Named sort presets for the Lifts "Sort by" dropdown (in addition to the clickable column headers).
const LIFT_SORT_OPTIONS = [
  { label: "Lift ID (default)", key: null, dir: "asc" as const },
  { label: "Next due (soonest first)", key: "nextDue", dir: "asc" as const },
  { label: "Last serviced (oldest first)", key: "lastServiced", dir: "asc" as const },
  { label: "Site (A-Z)", key: "site", dir: "asc" as const },
];

const emptyForm = {
  siteId: "",
  eleNumber: "",
  assetClass: "lift",
  liftType: "",
  driveType: "",
  frequency: "",
  manufacturer: "",
  serial: "",
  stops: "",
  capacityKg: "",
  lastServicedAt: "",
  nfcToken: "",
  simNumber: "",
  simExpiry: "",
  lolerDue: "",
  lolerCertExpiry: "",
  notes: "",
};

export function AssetsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [compliance, setCompliance] = useState<ComplianceFilter>("all");

  // The Services Due dashboard tile links here with ?filter=due-this-month.
  const dueThisMonth = searchParams.get("filter") === "due-this-month";
  const now = useMemo(() => new Date(), []);

  const serverFilters = useMemo(() => {
    const q: Record<string, string> = {};
    if (compliance !== "all") q.compliance = compliance;
    if (dueThisMonth) q.dueThisMonth = "true";
    return Object.keys(q).length > 0 ? q : undefined;
  }, [compliance, dueThisMonth]);

  function clearDueFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-2">
        {COMPLIANCE_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setCompliance(f.value)}
            className={`rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              compliance === f.value
                ? "border-ele-green bg-ele-inset text-ele-green"
                : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {dueThisMonth && (
        <span className="flex items-center gap-2 rounded-ele border border-ele-blue bg-ele-surface px-3 py-1.5 text-xs text-ele-blue">
          Showing lifts due this month
          <button onClick={clearDueFilter} className="text-ele-green hover:underline">Clear filter</button>
        </span>
      )}
    </div>
  );

  const fields: FieldDef[] = [
    {
      name: "siteId",
      label: "Site",
      type: "record-picker",
      pickerKind: "site",
      required: true,
    },
    { name: "eleNumber", label: "Lift ID (ELE number)", type: "text", help: "Leave blank until allocated at signing." },
    { name: "assetClass", label: "Asset class", type: "select", options: ASSET_KIND_OPTIONS },
    { name: "liftType", label: "Lift type", type: "text", help: "The kind of lift (passenger, goods, platform...), never the drive." },
    { name: "driveType", label: "Drive type", type: "select", options: DRIVE_TYPE },
    { name: "frequency", label: "Service frequency", type: "select", options: FREQUENCY },
    { name: "manufacturer", label: "Manufacturer", type: "text" },
    { name: "serial", label: "Serial number", type: "text" },
    { name: "stops", label: "Number of stops", type: "number" },
    { name: "capacityKg", label: "Capacity (kg)", type: "number" },
    {
      name: "lastServicedAt",
      label: "Last serviced",
      type: "date",
      help: "Set the real last service date to correct the PPM schedule for a migrated lift.",
    },
    { name: "nfcToken", label: "RFID token", type: "text", help: "Powers the customer service page." },
    { name: "simNumber", label: "SIM number", type: "text" },
    { name: "simExpiry", label: "SIM expiry", type: "date" },
    { name: "lolerDue", label: "LOLER due", type: "date" },
    { name: "lolerCertExpiry", label: "LOLER certificate expiry", type: "date" },
    { name: "notes", label: "Notes", type: "textarea" },
  ];

  return (
    <RegisterPage<Asset>
      title="Lifts and equipment"
      subtitle="Every lift and piece of LOLER equipment ELE looks after. Click a lift to open its full record."
      noun="lift"
      resource={assets}
      columns={columns}
      fields={fields}
      rowId={(a) => a.id}
      note={LIFTS_RESPONSIBILITY_NOTE}
      onRowClick={(a) => navigate(`/office/lifts/${a.id}`)}
      // Session ACW (mobile usability): below md keep the Lift ID, Site and the status dot; the
      // whole row already navigates to the lift detail page (onRowClick above), so RegisterPage
      // adds the mobile chevron disclosure column automatically once mobileColumns is set.
      // mobileColumns alone only HIDES the other columns -- it does not widen the three that
      // survive, and their <col> widths are the DESKTOP percentages (12% for Lift ID, 21% for
      // Site, sized for an 8-column table), which a hidden sibling never reclaims. At 375px that
      // is roughly 45px and 79px: measured live, "Lift ID" truncates to "L" and a real site name
      // ("Equitable House") to "Equitabl...". Session ALY adds mobileRow, the same two-line
      // swipe-row patch SitesPage already carries for the identical reason: below md the table
      // (headers included) is replaced entirely rather than squeezed, so the columns never
      // divide the width in the first place.
      mobileColumns={["ele", "site", "status"]}
      mobileLabel="Lifts and equipment"
      mobileRow={(a) => {
        const tone = liftStatusDot(
          {
            lolerDue: a.lolerDue ?? a.lolerCertExpiry,
            nextServiceDue: a.nextScheduledServiceAt ?? a.nextServiceDue,
            serviceOverdue: a.serviceOverdue,
          },
          now,
        );
        return {
          title: orNone(a.eleNumber),
          subtitle: orNone(a.site?.name),
          meta: <StatusPill domain="liftCompliance" status={tone} />,
          onTap: () => navigate(`/office/lifts/${a.id}`),
        };
      }}
      toForm={(a) => ({
        siteId: a.siteId ?? "",
        eleNumber: a.eleNumber ?? "",
        assetClass: a.assetClass ?? "lift",
        liftType: a.liftType ?? "",
        driveType: a.driveType ?? "",
        frequency: a.frequency ?? "",
        manufacturer: a.manufacturer ?? "",
        serial: a.serial ?? "",
        stops: a.stops != null ? String(a.stops) : "",
        capacityKg: a.capacityKg != null ? String(a.capacityKg) : "",
        lastServicedAt: toDateInput(a.lastServicedAt ?? null),
        nfcToken: a.nfcToken ?? "",
        simNumber: a.simNumber ?? "",
        simExpiry: toDateInput(a.simExpiry),
        lolerDue: toDateInput(a.lolerDue),
        lolerCertExpiry: toDateInput(a.lolerCertExpiry),
        notes: a.notes ?? "",
      })}
      emptyForm={emptyForm}
      toolbar={toolbar}
      sortOptions={LIFT_SORT_OPTIONS}
      // Session SC2C: the Lifts register is read 50 at a time and the SERVER applies the compliance and due-this-month filters, the search and
      // the sort (GET /api/assets, lib/registerLists.ts); assetMatchesFilter stays the reference the server is held equal to.
      serverPaged
      exportList="lifts"
      query={serverFilters}
    />
  );
}
